import React, { useEffect } from 'react';
import { useShopifyProduct } from '../../../../api/shopify/ShopifyProduct';
import { dataLayerVariantClickEvent, dataLayerVariantEcomEvent } from '../utils/utils';
import Buttons from './buttons/Buttons';
import Dropdown from './dropdown/Dropdown';
import Swatches from './swatches/Swatches';
import { toCamelCase } from '@jbhi-fi/utils/src/toCamelCase';
import { getMarketplaceTrackingValuesFromShopify } from '../../../../utils/event-tracking/globalTrackingHelpers';
import { FamilyOption, Family } from '@jbhi-fi/apis/src/shopify/types/OnlineProduct';
import { Coupon } from '@jbhi-fi/components/src/types';
import { styles } from './Variants.css';

type VariantsProps = {
  coupons?: Array<Coupon>;
  className?: string;
  hasQuantitySelect?: boolean;
};

const Variants = ({ coupons, hasQuantitySelect = false }: VariantsProps): JSX.Element => {
  const { productMetafields, setShopify, setMetafields, marketplaceSellers, shopify } = useShopifyProduct();
  const variantData = (productMetafields?.Family || window.Jbhifi['product.metafields']?.family || {}) as Family;
  const colourVariantName = 'Colour';

  const getProductData = async (handle: string): Promise<any> => {
    try {
      const jsonResponse = await fetch(`/products/${handle}?view=json`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return jsonResponse.json();
    } catch (error) {
      console.error('Error while getting product data: ', error);
    }
  };

  //set the currently viewed product
  const setProduct = (jsonResponse, handle, setHistory) => {
    if (jsonResponse && jsonResponse.product && jsonResponse.metafields) {
      // set the previous sku in context before product update
      const { value: product } = jsonResponse.metafields.online_product;
      window.Product.productGroup = product?.ProductGroup?.Id?.toString();
      window.Product.sku = product.Id;
      window.Jbhifi.sku = product.Id;
      setShopify(jsonResponse.product);
      setMetafields(jsonResponse.metafields);
      const relatedProdElement: HTMLElement = document.querySelector('.pdp-related-skus');
      if (relatedProdElement) {
        relatedProdElement.removeAttribute('data-related-skus');
        if (product.Display.RelatedProductIds && product.Display.RelatedProductIds.length > 0) {
          relatedProdElement.dataset.relatedSkus = product.Display.RelatedProductIds.join(',');
        }
      }
      if (setHistory) {
        window.history.pushState({ prevUrl: location.href }, jsonResponse.product.title, `/products/${handle}`);
      }
      window.document.title = jsonResponse.product.title;
    } else {
      window.location.href = `/products/${handle}`;
    }
  };

  const isProductInFamilyOptions = (options: Array<FamilyOption>, sku: string | undefined) => {
    return options.some(o => o['Children'].some(c => (c['Sku'] ?? c['SKU']) === sku));
  };

  const productHasColourVariant = (name, variantTypes) => {
    return name !== colourVariantName && variantTypes.some(variantType => variantType.name === colourVariantName);
  };

  //set the shopify product after back button clicked
  const handleHistory = async (): Promise<void> => {
    const handle = /[^/]*$/.exec(window.location.href)[0].split('?')[0]; // get just the product from the URL, removing anything before the last / and any query string
    const jsonResponse = await getProductData(handle);
    setProduct(jsonResponse, handle, false);
  };

  //listen for back button being clicked
  useEffect(() => {
    window.addEventListener('popstate', handleHistory);
    return () => window.removeEventListener('popstate', handleHistory);
  }, []);

  // If no variants to display, return nothing
  if (Object.keys(variantData).length === 0 || !isProductInFamilyOptions(variantData.Options, productMetafields?.Id)) {
    return null;
  }

  const isPerksMember = window.Jbhifi?.customerDetails?.isPerks;

  const allProducts = variantData['Handles']
    .map((variant, index: number) => {
      const handleVariant = toCamelCase(variant);
      // Get variant name as key and {value, sequence}
      const variantPairs = variantData.Options.reduce((accumulated, currentValue) => {
        const option = variantData['Options'].find(o => o['Name'] === currentValue.Name); //get variants matching type (name) eg. "Colour"
        if (!option) return accumulated;
        const child = option['Children']?.find(c => (c['Sku'] ?? c['SKU']) === handleVariant.sku);
        if (!child) return accumulated;
        const sequence = child['Sequence'] ?? ++index;
        if (!sequence) return accumulated;
        const value = child['Value']?.trim() || ''; // handle metafields.online_product.value.Family.Options['Color'].Children not having the Value property set
        return {
          ...accumulated,
          [currentValue.Name]: {
            value,
            sequence,
          },
        };
      }, {});

      const hasPerksPrice = Boolean(
        variantData?.Prices?.find(({ Sku: sku }) => sku === handleVariant.sku)?.PerksDisplayPriceInc
      );

      const perksPrice = variantData.Prices?.find(({ Sku: sku }) => sku === handleVariant.sku)?.PerksDisplayPriceInc;
      const coreTicketPrice = variantData.Prices?.find(({ Sku: sku }) => sku === handleVariant.sku)?.CoreTicketPrice;
      const standardPrice = variantData.Prices?.find(({ Sku: sku }) => sku === handleVariant.sku)?.DisplayPriceInc;
      const imageUrl = toCamelCase(variantData.ImageUrls).find(
        ({ sku }: { sku: string }) => sku === handleVariant.sku
      )?.image;

      // Return a nice object with everything we need
      return {
        sku: handleVariant.sku,
        handle: handleVariant.handle,
        title: variantData.Title,
        imageUrl,
        availability: variantData.Availabilities?.find(({ SKU: sku }) => sku === handleVariant.sku),
        price: hasPerksPrice && isPerksMember ? perksPrice : standardPrice,
        corePrice: coreTicketPrice ?? standardPrice,
        ...variantPairs,
        current: handleVariant.sku === productMetafields?.Id,
      };
    })
    .filter(Boolean);

  // Get variant options
  const variantTypes = variantData['Options']
    .map(({ Name: name, SpecRender: type }) => ({
      name,
      type,
    }))
    .sort((a, b) => (a.name === colourVariantName ? -1 : b.name === colourVariantName ? 1 : 0));

  // Append relevant variant children according to variant type and current product
  const allVariants = variantTypes.map(({ name, type }, index) => {
    const group = { name, type, children: [] };
    const optionChildren = allProducts.filter(product => product[name]);
    const currentVariant = optionChildren.find(optionChild => optionChild.current);

    if (!currentVariant) {
      return group;
    }

    const optionChildrenRelevant = optionChildren.filter(child => {
      for (let i = 0; i < index; i++) {
        const prev = variantTypes[i].name;
        if (child[prev]?.value !== currentVariant[prev]?.value) {
          return false;
        }
      }
      return true;
    });

    const optionChildrenFiltered = productHasColourVariant(name, variantTypes)
      ? optionChildrenRelevant
      : optionChildren; // always show Colour swatches, but hide other variant types not applicable to current SKU
    const optionChildrenDeDuped = optionChildrenFiltered.filter(child => child.current); // before de-duping, ensure current SKU isn't excluded
    optionChildrenFiltered.map(child => {
      const i = optionChildrenDeDuped.findIndex(c => c[name].value === child[name].value);
      if (i !== -1) {
        const existingVariant = optionChildrenDeDuped[i];
        if (!optionChildrenDeDuped[i].current) {
          const optScoreOld = variantTypes.filter(
            opt => existingVariant[opt.name]?.value === currentVariant[opt.name]?.value
          ).length;
          const optScoreNew = variantTypes.filter(
            opt => child[opt.name]?.value === currentVariant[opt.name]?.value
          ).length;
          // keep the variant that most closely matches the current selection
          if (optScoreNew > optScoreOld) {
            optionChildrenDeDuped.splice(i, 1, child);
          }
        }
      } else {
        optionChildrenDeDuped.push(child);
      }
    });
    group.children = optionChildrenDeDuped.sort((a, b) => a[name].sequence - b[name].sequence);
    return group;
  });

  //called upon selecting a product from the variant swatch
  const switchProduct = async (handle, variantName): Promise<void> => {
    if (!handle) {
      return;
    }

    // Hacky way to get the JSON from any product
    const jsonResponse = await getProductData(handle);

    // create props for variant click event for GTM
    const responseOnlineValue = jsonResponse?.metafields?.online_product?.value;
    const sku = productMetafields?.Id;
    const newSku = responseOnlineValue?.Id;
    const productName = responseOnlineValue?.Display && responseOnlineValue?.Display?.Description;
    const category = responseOnlineValue?.Category?.Name;
    const type = jsonResponse?.product?.type;
    const variant = jsonResponse?.product?.variants?.find(variant => variant?.sku === responseOnlineValue?.Id)?.name;
    const productGroup = productMetafields?.ProductGroup?.Description;

    // create further props for variant ecommerce event for GTM
    const googleTagManager = responseOnlineValue?.ProductTags?.GoogleTagManager;

    // Push GTM variant dataLayer click event
    dataLayerVariantClickEvent(
      sku,
      newSku,
      productName,
      category,
      type,
      variant,
      googleTagManager,
      variantName,
      productGroup
    );

    const ecommerceCartItemProps = getMarketplaceTrackingValuesFromShopify({
      product: shopify,
      onlineProduct: productMetafields,
      marketplaceSellers,
    });

    // Push GTM variant dataLayer ecommerce event
    dataLayerVariantEcomEvent(sku, googleTagManager, ecommerceCartItemProps, coupons);
    setProduct(jsonResponse, handle, true);
  };

  return (
    <div className={styles.container({ hasQuantitySelect })}>
      {allVariants
        .filter(({ children }) => children.length > 1)
        .map(variant => {
          return {
            swatches: (
              <Swatches
                key={`${variantData.Id}-${variant.name}-${variant.type}`}
                name={variant.name}
                variants={variant.children}
                switchProduct={switchProduct}
              />
            ),
            buttons: (
              <Buttons
                key={`${variantData.Id}-${variant.name}-${variant.type}`}
                name={variant.name}
                variants={variant.children}
                switchProduct={switchProduct}
              />
            ),
            dropdown: (
              <Dropdown
                key={`${variantData.Id}-${variant.name}-${variant.type}`}
                name={variant.name}
                variants={variant.children}
                switchProduct={switchProduct}
              />
            ),
          }[variant.type.toLowerCase()];
        })}
    </div>
  );
};

export default Variants;
